import { Observable, OperatorFunction } from 'rxjs';

/**
 * 전역 인터셉터가 삼킨 응답을 되살릴 때 쓰는 에러.
 *
 * `error-http-interceptor.ts` 는 502 를 새로고침 팝업으로 처리하고 `EMPTY` 를 반환한다.
 * 그래서 502 응답은 `next` 도 `error` 도 없이 곧바로 complete 되고,
 * 완료 처리를 `next`/`error` 에만 걸어 둔 호출부는 영영 호출되지 않는다.
 */
export class SwallowedHttpError extends Error {
  constructor(message = '일시적인 오류로 요청이 처리되지 않았어요. 잠시 후 다시 시도해주세요.') {
    super(message);
    this.name = 'SwallowedHttpError';
  }
}

/**
 * 값 하나 없이 끝난 HTTP 스트림을 에러로 되살린다.
 *
 * 인터셉터가 502 를 `EMPTY` 로 삼키면 호출부의 `error` 콜백이 돌지 않아
 * 로딩 플래그가 안 풀리거나 캐시가 안 비워지는 식으로 화면이 멈춘다.
 * 응답 본문이 없는 요청(204 등)에는 쓰지 않는다 — 정상 완료까지 에러로 바뀐다.
 */
export function failIfSwallowed<T>(message?: string): OperatorFunction<T, T> {
  return (source) =>
    new Observable<T>((subscriber) => {
      let emitted = false;
      return source.subscribe({
        next: (value) => {
          emitted = true;
          subscriber.next(value);
        },
        error: (err) => subscriber.error(err),
        complete: () => {
          if (emitted) {
            subscriber.complete();
          } else {
            subscriber.error(new SwallowedHttpError(message));
          }
        },
      });
    });
}
